import React, { useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import moment from 'moment';
import Ipms from '../../images/ipms.svg';
import { API } from '../../apis/api';
import { API_RESOURCE_URLS, ASN_STATUS, ZPL_ASN_TEMPLATE_FOR_DROPPING } from '../../constants';
import '../InformationRadiator/InformationRadiator.css';
import './SubAssemblyView.css';
import { useAjaxCallbackWithIntervals } from '../../hooks';
import {
  getFormattedAsn,
  splitDescription,
  compareByDateAndSerialNumber,
  getSelectedOptionalColumns,
} from '../../utils/tableUtils';
import { usePopupManager } from '../../providers/PopupManager/PopupManager';
import { usePlantAndLinesStore } from '../../providers/PlantAndLinesStore/PlantAndLinesStore';
import LineDropdownSelector from '../LineDropdownSelector/LineDropdownSelector';
import AttributesSelector from '../AttributesSelector/AttributesSelector';
import { optionalColumns } from '../../staticData';
import { Link, useLocation } from 'react-router-dom';
import { buildErrorMessage, addSubAssemblyDrooping, getSubAssemblyConfigurations } from '../../apis/calls';

const SubAssemblyView = () => {
  const location = useLocation();

  const DROPPED_ROWS_COUNT = 30;

  const [selectedOptionalColNames, setSelectedOptionalColNames] = useState([]);

  const activeOptionalColumns = useMemo(() => {
    return getSelectedOptionalColumns(optionalColumns, selectedOptionalColNames, {});
  }, [selectedOptionalColNames]);

  const [plant] = usePlantAndLinesStore().plantState;
  const [line] = usePlantAndLinesStore().lineState;

  const latestLineValueRef = useRef(line.value);
  const latestPlantValueRef = useRef(plant.value);

  const { showInternalError, showError } = usePopupManager();

  const [droppedRowsData, setDroppedRowsData] = useState({
    data: [],
    loading: false,
  });

  const [readyForDropRowsData, setReadyForDropRowsData] = useState({
    data: [],
    loading: false,
  });
  
  const [printStatusData, setPrintStatusData] = useState({});
  
  const SUB_ASSEMBLY_STORAGE_KEY = 'sub_assembly_view_data';

  const getInitialSubAssemblyState = (locState) => {
    if (locState && locState.subAssembly) {
      const data = {
        subAssembly: locState.subAssembly,
        dropStartSeq: locState.dropStartSeq !== undefined && locState.dropStartSeq !== null ? String(locState.dropStartSeq) : '0',
        dropEndSeq: locState.dropEndSeq !== undefined && locState.dropEndSeq !== null ? String(locState.dropEndSeq) : '30',
        readyForDropFlag: locState.readyForDropFlag,
      };
      try {
        localStorage.setItem(SUB_ASSEMBLY_STORAGE_KEY, JSON.stringify(data));
      } catch (e) {
        console.error('Failed to save subAssembly to localStorage', e);
      }
      return data;
    }
    try {
      const saved = localStorage.getItem(SUB_ASSEMBLY_STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Failed to read subAssembly from localStorage', e);
    }
    return null;
  };

  const savedSubData = getInitialSubAssemblyState(location?.state);

  const [subAssemblyName, setSubAssemblyName] = useState(savedSubData?.subAssembly || '');

  const isLucknowProlifePlant = plant?.label === 'Lucknow-Prolife';
  const parentName = isLucknowProlifePlant ? 'Parent Part' : 'VC';

  const initialFromSeq = savedSubData?.dropStartSeq !== undefined && savedSubData?.dropStartSeq !== null
    ? String(savedSubData.dropStartSeq)
    : '0';
  const initialToSeq = savedSubData?.dropEndSeq !== undefined && savedSubData?.dropEndSeq !== null
    ? String(savedSubData.dropEndSeq)
    : '30';

  // --- Filter states ---
  const [selectedFromSequence, setSelectedFromSequence] = useState(initialFromSeq);
  const [selectedToSequence, setSelectedToSequence] = useState(initialToSeq);
  const [selectedStatus, setSelectedStatus] = useState(null);

  const [appliedFromSequence, setappliedFromSequence] = useState(Number(initialFromSeq));
  const [appliedToSequence, setappliedToSequence] = useState(Number(initialToSeq));
  const [appliedStatus, setAppliedStatus] = useState(null);

  useEffect(() => {
    if (location?.state?.subAssembly) {
      const data = {
        subAssembly: location.state.subAssembly,
        dropStartSeq: location.state.dropStartSeq !== undefined && location.state.dropStartSeq !== null ? String(location.state.dropStartSeq) : '0',
        dropEndSeq: location.state.dropEndSeq !== undefined && location.state.dropEndSeq !== null ? String(location.state.dropEndSeq) : '30',
        readyForDropFlag: location.state.readyForDropFlag,
      };
      setSubAssemblyName(data.subAssembly);
      setSelectedFromSequence(data.dropStartSeq);
      setSelectedToSequence(data.dropEndSeq);
      setappliedFromSequence(Number(data.dropStartSeq));
      setappliedToSequence(Number(data.dropEndSeq));
      try {
        localStorage.setItem(SUB_ASSEMBLY_STORAGE_KEY, JSON.stringify(data));
      } catch (e) {
        console.error('Failed to update subAssembly in localStorage', e);
      }
    }
  }, [location.state.dropEndSeq, location.state.dropStartSeq, location.state.readyForDropFlag, location.state.subAssembly]);

  useEffect(() => {
    if (!plant?.value || !line?.value) return;

    const fetchConfigForLine = async () => {
      try {
        const response = await getSubAssemblyConfigurations(plant.value, line.value);
        if (response && response.data && Array.isArray(response.data) && response.data.length > 0) {
          const currentSub = location?.state?.subAssembly || subAssemblyName || savedSubData?.subAssembly;
          const config = response.data.find(c => c.subAssemblyName === currentSub) || response.data[0];
          if (config) {
            const activeSub = config.subAssemblyName;
            const startSeq = config.dropStartSeq !== undefined ? String(config.dropStartSeq) : '0';
            const endSeq = config.dropEndSeq !== undefined ? String(config.dropEndSeq) : '30';

            setSubAssemblyName(activeSub);
            setSelectedFromSequence(startSeq);
            setSelectedToSequence(endSeq);
            setappliedFromSequence(Number(startSeq));
            setappliedToSequence(Number(endSeq));

            const dataToSave = {
              subAssembly: activeSub,
              dropStartSeq: startSeq,
              dropEndSeq: endSeq,
              readyForDropFlag: config.readyForDropFlag,
            };
            try {
              localStorage.setItem(SUB_ASSEMBLY_STORAGE_KEY, JSON.stringify(dataToSave));
            } catch (e) {
              console.error('Failed to save subAssembly to localStorage', e);
            }
          }
        }
      } catch (err) {
        console.error('Failed to fetch config for line in SubAssemblyView', err);
      }
    };

    fetchConfigForLine();
  }, [line.value, location.state.subAssembly, plant.value, savedSubData.subAssembly, subAssemblyName]);

  // --- Validation states for sequence inputs ---
  const [fromSeqError, setFromSeqError] = useState('');
  const [toSeqError, setToSeqError] = useState('');

  const validateSeqValue = (value) => {
    if (value === '') return '';
    if (!/^\d+$/.test(value)) return 'Only integers allowed';
    const num = parseInt(value, 10);
    if (num < 0 || num > 99) return 'Value must be 0-99';
    return '';
  };

  const handleFromSeqChange = (e) => {
    const val = e.target.value;
    setSelectedFromSequence(val);
    setFromSeqError(validateSeqValue(val));
  };

  const handleToSeqChange = (e) => {
    const val = e.target.value;
    setSelectedToSequence(val);
    setToSeqError(validateSeqValue(val));
  };

  // --- Dynamic Count Summary from API ---
  const [countSummary, setCountSummary] = useState({ lineup: 30, dropped: 30 });

  // --- Status filter options ---
  const statusFilterOptions = [
    { value: 'READY_FOR_DROP', label: 'Ready For Drop' },
    { value: 'DROPPED', label: 'Dropped' },
  ];

  // --- Apply filters ---
  const filteredReadyForDropData = useMemo(() => {
    return readyForDropRowsData.data || [];
  }, [readyForDropRowsData.data]);

  const filteredDroppedData = useMemo(() => {
    return droppedRowsData.data || [];
  }, [droppedRowsData.data]);

  const READY_FOR_DROP_ROWS_COUNT = filteredReadyForDropData.length;
  const FILTERED_DROPPED_ROWS_COUNT = filteredDroppedData.length || DROPPED_ROWS_COUNT;

  // --- Clear filters handler ---
  const handleClearFilters = () => {
    setSelectedFromSequence('');
    setSelectedToSequence('');
    setFromSeqError('');
    setToSeqError('');
    setSelectedStatus(null);
    setappliedFromSequence(null);
    setappliedToSequence(null);
    setAppliedStatus(null);
  };

  const handleStatusChange = (val) => {
    const statusVal = val?.value || null;
    setSelectedStatus(val);
    setSelectedFromSequence('');
    setSelectedToSequence('');
    setFromSeqError('');
    setToSeqError('');
    setappliedFromSequence(null);
    setappliedToSequence(null);
    setAppliedStatus(statusVal);
  };

  const isFilterActive =
    selectedFromSequence !== '' ||
    selectedToSequence !== '' ||
    selectedStatus !== null ||
    appliedFromSequence !== null ||
    appliedToSequence !== null ||
    appliedStatus !== null;

  const checkIsPrinted = (row) => {
    if (!row) return false;
    const currentSub = subAssemblyName || location?.state?.subAssembly;

    if (Array.isArray(row.subassembly) && row.subassembly.length > 0) {
      if (currentSub) {
        const sub = row.subassembly.find(
          (s) => s.subAssemblyName?.toLowerCase().trim() === currentSub.toLowerCase().trim()
        );
        return Boolean(sub?.printFlag);
      }
      return row.subassembly.some((s) => s.printFlag);
    }

    const asnKey = row.asn || row.asnId;
    if (asnKey && printStatusData[asnKey]) {
      if (currentSub && printStatusData[asnKey][currentSub]) {
        return printStatusData[asnKey][currentSub] === 'PRINT';
      }
      return printStatusData[asnKey].printStatus === 'PRINT';
    }

    return false;
  };

  const canPrintRow = (row) => {
    if (!row || (!row.asn && !row.asnId)) return false;
    if (checkIsPrinted(row)) return true;

    const rowIndex = filteredReadyForDropData.indexOf(row);
    const lastIndex = filteredReadyForDropData.length - 1;
    if (rowIndex === lastIndex) return true;

    for (let i = rowIndex + 1; i <= lastIndex; i++) {
      const nextRow = filteredReadyForDropData[i];
      if (nextRow && (nextRow.asn || nextRow.asnId) && !checkIsPrinted(nextRow)) {
        return false;
      }
    }
    return true;
  };

  const handlePrintClick = async (row) => {
    if (!row || (!row.asn && !row.asnId)) return;

    if (!canPrintRow(row)) {
      showError({
        contextText: 'Sequential Print Required',
        info: 'Please print the previous rows first. Printing must be done in sequence order.',
      });
      return;
    }
    const targetAsnId = row.asnId || row.asn;
    const targetAsnCode = row.asn || row.asnId;
    const vc = row.materialNumber || row.vc || '';
    const description = row.materialDescription || row.vcAlias || '';
    const subAssemblyNameToUse = subAssemblyName || location?.state?.subAssembly || row.subAssemblyName || '';

    try {
      if (plant?.value && line?.value) {
        await addSubAssemblyDrooping(plant.value, line.value, {
          asnId: targetAsnId,
          subAssemblyName: subAssemblyNameToUse,
          printFlag: true,
          status: row.status || 'READY',
        });
      }

      setPrintStatusData((prev) => ({
        ...prev,
        [targetAsnId]: { ...prev[targetAsnId], [subAssemblyNameToUse]: 'PRINT', printStatus: 'PRINT' },
        [targetAsnCode]: { ...prev[targetAsnCode], [subAssemblyNameToUse]: 'PRINT', printStatus: 'PRINT' },
      }));

      if (plant?.value && line?.value) {
        await loadRows(line.value, plant.value, appliedFromSequence, appliedToSequence, appliedStatus);
      }

      const label = ZPL_ASN_TEMPLATE_FOR_DROPPING
        .replaceAll('{LineID}', line?.label || '')
        .replaceAll('{ASN}', getFormattedAsn(row) || targetAsnCode)
        .replaceAll('{MaterialNumber}', vc)
        .replaceAll('{MaterialDescription}', description)
        .replaceAll('{EngineMaterialDescription}', '')
        .replaceAll('{EngineMaterialNumber}', '');

      const zplstring = encodeURIComponent(label);
      window.location.href = `nirmaan://?cmd=print&zplstring=${zplstring}`;
    } catch (error) {
      console.error('Failed to update print status or trigger print', error);
      if (showError) showError({ error });
    }
  };

  const getAsnsByStatusForCount = async (status, count, optionalParams) => {
    if (plant.value == null) return;
    if (line.value == null) return;

    let url = API_RESOURCE_URLS.getDroppingUpdate(
      plant.value,
      line.value,
      status,
      count,
    );

    if (optionalParams?.from !== undefined && optionalParams?.from !== null) {
      url += `&from=${optionalParams.from}`;
    }
    if (optionalParams?.to !== undefined && optionalParams?.to !== null) {
      url += `&to=${optionalParams.to}`;
    }

    const response = await API.get(url);

    return response.data;
  };

  const prepareLastDroppedAsnRows = (asns, requiredNoOfRows) => {
    let sortDateByDesc =
      asns &&
      asns.sort((a, b) => {
        if (
          a.dropTimestamp != null &&
          a !== undefined &&
          b.dropTimestamp != null &&
          b !== undefined
        )
          return (
            new Date(...b?.dropTimestamp.split('/').reverse()) -
            new Date(...a?.dropTimestamp.split('/').reverse())
          );
        else return [];
      });

    const startPos = appliedFromSequence !== null ? appliedFromSequence : 1;
    let position = startPos - 1;
    const addPositionValue = sortDateByDesc?.map(FormatedValue => {
      position = position + 1;
      return { ...FormatedValue, position: position };
    });

    const positionToAsnMap = {};

    addPositionValue &&
      addPositionValue.forEach(asn => (positionToAsnMap[asn.position] = asn));

    const rowsInAscendingOrder = [...Array(requiredNoOfRows).keys()].map(
      (_, index) => {
        return positionToAsnMap[index + startPos];
      },
    );
    return rowsInAscendingOrder;
  };

  const loadLastDroppedAsn = async (fromSequenceNumber, toSequenceNumber) => {
    setDroppedRowsData(prevState => ({ ...prevState, loading: true }));
    try {
      const noOfRows = (fromSequenceNumber && toSequenceNumber) ? (toSequenceNumber - fromSequenceNumber + 1) : 30;

      const asns = await getAsnsByStatusForCount(ASN_STATUS.DROPPED, noOfRows, {
        from: fromSequenceNumber,
        to: toSequenceNumber,
      });
      if (
        plant.value !== latestPlantValueRef.current ||
        line.value !== latestLineValueRef.current
      ) {
        setDroppedRowsData(prevState => ({
          ...prevState,
          loading: false,
        }));
        return [];
      }

      const positionallyFormattedRows = prepareLastDroppedAsnRows(
        asns,
        noOfRows,
      );

      setDroppedRowsData({
        data: positionallyFormattedRows,
        loading: false,
      });
      return positionallyFormattedRows;
    } catch (err) {
      console.error(
        'failed to load last dropped asn, err:' + buildErrorMessage(err),
      );
      setDroppedRowsData({
        data: [],
        loading: false,
      });
      showInternalError();
      return [];
    }
  };

  const prepareReadyForDropRows = (data, requiredNoOfRows) => {
    const positionToAsnMap = {};
    data && data.forEach(asn => (positionToAsnMap[asn.position] = asn));
    const rowsInDescendingOrder = [...Array(requiredNoOfRows).keys()]
      .map((_, index) => positionToAsnMap[index + 1] || { position: index + 1 })
      .sort((a, b) => b.position - a.position);
    return rowsInDescendingOrder;
  };

  const loadFirstReadyForDroppedAsn = async (fromSequenceNumber, toSequenceNumber) => {
    setReadyForDropRowsData(prevState => ({ ...prevState, loading: true }));
    try {
      const noOfRows = (fromSequenceNumber && toSequenceNumber) ? (toSequenceNumber - fromSequenceNumber + 1) : 15;
      const asns = await getAsnsByStatusForCount(
        ASN_STATUS.READY_FOR_DROP,
        noOfRows,
        {
          from: fromSequenceNumber,
          to: toSequenceNumber,
        },
      );
      const validAsns = Array.isArray(asns) ? asns : [];
      const noOfRowsForAsn = validAsns.length;

      if (
        plant.value !== latestPlantValueRef.current ||
        line.value !== latestLineValueRef.current
      ) {
        setReadyForDropRowsData(prevState => ({
          ...prevState,
          loading: false,
        }));
        return [];
      }

      const sortedData = asns?.sort((a, b) =>
        compareByDateAndSerialNumber(a, b, 'productionDate', 'sequence'),
      );

      let position = 0;
      const addPositionValue = sortedData?.map(FormatedValue => {
        position = position + 1;
        return { ...FormatedValue, position: position };
      });

      const positionallyFormattedRows = prepareReadyForDropRows(
        addPositionValue,
        noOfRowsForAsn,
      );
      setReadyForDropRowsData({
        data: positionallyFormattedRows,
        loading: false,
      });
      return positionallyFormattedRows;
    } catch (err) {
      console.error(
        'failed to load last ready for drop asn, err:' + buildErrorMessage(err),
      );
      setReadyForDropRowsData({
        data: [],
        loading: false,
      });
      showInternalError();
      return [];
    }
  };

  const loadRows = async (lineVal, plantVal, fromSequenceNumber, toSequenceNumber, statusVal) => {
    let readyPromise = Promise.resolve([]);
    if (!statusVal || statusVal === 'READY_FOR_DROP') {
      // Ready For Drop table always loads without sequence filters
      readyPromise = loadFirstReadyForDroppedAsn(null, null);
    } else {
      setReadyForDropRowsData({ data: [], loading: false });
    }

    let droppedPromise = Promise.resolve([]);
    if (!statusVal || statusVal === 'DROPPED') {
      // Only DROPPED CHASSIS table uses From/To Seq filters
      droppedPromise = loadLastDroppedAsn(fromSequenceNumber, toSequenceNumber);
    } else {
      setDroppedRowsData({ data: [], loading: false });
    }

    await Promise.all([readyPromise, droppedPromise]);
  };

  useEffect(() => {
    latestLineValueRef.current = line.value;
    latestPlantValueRef.current = plant.value;
  }, [plant.value, line.value]);

  const lastRefreshedDate = useAjaxCallbackWithIntervals(
    droppedRowsData.loading || readyForDropRowsData.loading,
    false,
    loadRows,
    line.value,
    plant.value,
    appliedFromSequence,
    appliedToSequence,
    appliedStatus,
  );

  const handleSearch = () => {
    // Validate before searching
    const fromErr = validateSeqValue(selectedFromSequence);
    const toErr = validateSeqValue(selectedToSequence);
    setFromSeqError(fromErr);
    setToSeqError(toErr);

    if (fromErr || toErr) return;

    const fromSequenceNumber = selectedFromSequence !== '' ? parseInt(selectedFromSequence, 10) : null;
    const toSequenceNumber = selectedToSequence !== '' ? parseInt(selectedToSequence, 10) : null;
    const filterStatusValue = selectedStatus?.value || null;

    if (fromSequenceNumber !== null && toSequenceNumber !== null && fromSequenceNumber > toSequenceNumber) {
      showError({
        contextText: 'Invalid Sequence Range',
        info: 'From Sequence cannot be greater than To Sequence.',
      });
      return;
    }

    setappliedFromSequence(fromSequenceNumber);
    setappliedToSequence(toSequenceNumber);
    setAppliedStatus(filterStatusValue);
  };

  const getPaddedPositionString = positionIndex =>
    String(positionIndex).padStart(2, '0');

  // Compute the station label for the dropped table based on applied filters
  const getDroppedStationLabel = () => {
    if (appliedFromSequence !== null && appliedToSequence !== null) {
      return `Seq ${appliedFromSequence}-${appliedToSequence}`;
    }
    if (appliedFromSequence !== null) {
      return `From ${appliedFromSequence}`;
    }
    if (appliedToSequence !== null) {
      return `To ${appliedToSequence}`;
    }
    return 'Station';
  };

  const getAttributeValue = (row, col) => {
    if (!row) return '-';
    const field = col.field;

    if (field === 'colour') {
      const desc = row.attributes?.colourDescription || row.colourDescription;
      const code = row.attributes?.colourCode || row.colourCode;
      if (desc && code) return `${desc} (${code})`;
      if (desc) return desc;
      if (code) return code;
    }

    const valFromAttr = row.attributes ? row.attributes[field] : undefined;
    const valFromRow = row[field];
    const val = valFromAttr !== undefined && valFromAttr !== null ? valFromAttr : valFromRow;

    if (val === undefined || val === null || val === '') return '-';
    return String(val);
  };

  const renderRow = index => {
    const row = filteredDroppedData[index];

    const [descriptionHead, descriptionTail] = splitDescription(
      row && row.materialDescription,
    );

    const startPos = appliedFromSequence !== null ? appliedFromSequence : 1;
    const position = row ? row.position : index + startPos;
    const isPrinted = checkIsPrinted(row);

    return (
      <tr key={index}>
        <td width="7%" height="30" align="center" valign="middle">
          {index === 0 ? 'Station' : <>&nbsp;</>}
        </td>
        <td width="5%" align="center" valign="middle">
          {getPaddedPositionString(position)}
        </td>
        <td width="8%" align="center" valign="middle">
          {isPrinted && (
            <div className="indicator-dot printed" title="Printed"></div>
          )}
        </td>
        <td
          width="10%"
          align="center"
          valign="middle"
          className="asn"
          data-testid={`select-Dropped-asn-${index}`}
        >
          {getFormattedAsn(row)}
        </td>
        <td
          align="left"
          valign="middle"
          width={activeOptionalColumns.length > 0 ? '32%' : '45%'}
          className="descriptionCell"
        >
          <span className="blue">{descriptionHead || '-'}</span>{' '}
          {descriptionTail || '-'}
        </td>
        <td width={activeOptionalColumns.length > 0 ? '16%' : '25%'}>
          {row && row.materialNumber ? row.materialNumber : '-'}
        </td>
        {activeOptionalColumns.map((col) => (
          <td
            key={col.name}
            align="center"
            valign="middle"
            style={{ minWidth: '110px', padding: '0 16px', whiteSpace: 'nowrap' }}
          >
            {getAttributeValue(row, col)}
          </td>
        ))}
      </tr>
    );
  };

  const renderReadyForDropRow = index => {
    const row = filteredReadyForDropData[index];
    const [descriptionHead, descriptionTail] = splitDescription(
      row && row.materialDescription,
    );

    const position =
      row && row.position ? row.position : READY_FOR_DROP_ROWS_COUNT - index;

    const conditionalBorderClass =
      index + 1 > 0 && (index + 1) % 5 === 0 && 'borderBt';
      
    const isPrinted = checkIsPrinted(row);

    return (
      <tr className="borderBt" key={index} data-testid={`select-row-${index}`}>
        <td
          className={conditionalBorderClass}
          width="7%"
          height="30"
          align="center"
          valign="middle"
        >
          {index === READY_FOR_DROP_ROWS_COUNT - 1 ? 'LineUp' : <>&nbsp;</>}
        </td>
        <td
          className={conditionalBorderClass}
          width="5%"
          height="30"
          align="center"
          valign="middle"
        >
          {getPaddedPositionString(position)}
        </td>
        <td
          className={conditionalBorderClass}
          width="8%"
          height="30"
          align="center"
          valign="middle"
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
            <button
              className="action-btn print"
              onClick={() => handlePrintClick(row)}
              disabled={!row || !row.asn || !canPrintRow(row)}
              title={row && row.asn && !canPrintRow(row) ? 'Print previous rows first' : ''}
            >
              Print
            </button>
            {isPrinted && (
              <div className="indicator-dot printed" title="Printed"></div>
            )}
          </div>
        </td>
        <td
          className={conditionalBorderClass}
          width="10%"
          height="30"
          align="center"
          valign="middle"
          data-testid={`select-readyForDrop-asn-${index}`}
        >
          <span className="asn">{getFormattedAsn(row)}</span>
        </td>
        <td
          className={`${conditionalBorderClass ? conditionalBorderClass : ''} descriptionCell`.trim()}
          width={activeOptionalColumns.length > 0 ? '32%' : '45%'}
          align="left"
          valign="middle"
        >
          <span className="blue">{descriptionHead || '-'}</span>{' '}
          {descriptionTail || '-'}
        </td>
        <td
          className={conditionalBorderClass}
          width={activeOptionalColumns.length > 0 ? '16%' : '25%'}
          align="left"
          valign="middle"
        >
          {row && row.materialNumber ? row.materialNumber : '-'}
        </td>
        {activeOptionalColumns.map((col) => (
          <td
            key={col.name}
            className={conditionalBorderClass}
            align="center"
            valign="middle"
            style={{ minWidth: '110px', padding: '0 16px', whiteSpace: 'nowrap' }}
          >
            {getAttributeValue(row, col)}
          </td>
        ))}
      </tr>
    );
  };

  return (
    <div>
      <div>
        <table
          width="98%"
          border="0"
          align="center"
          cellpadding="0"
          cellspacing="0"
          className="display_table"
        >
          <tr>
            <td>
              <div className="ir-toolbar">
                  <div className="ir-toolbar__item ir-toolbar__liveUpdate">
                    <span className="thead">LIVE UPDATE</span>
                    <strong className="date">
                      {moment().format('DD MMMM').toUpperCase()}{' '}
                      {moment().format('YYYY')}
                    </strong>
                  </div>
                  <div className="ir-toolbar__item ir-toolbar__title">
                    <span className="thead">Production Broadcast</span>
                    <strong>{subAssemblyName ? `${subAssemblyName} Dropping Sequence` : (location?.state?.subAssembly ? `${location.state.subAssembly} Dropping Sequence` : 'Sub Assembly View')}</strong>
                  </div>
                  <div className="ir-toolbar__item ir-toolbar__line">
                    <span className="filterLabel">Select Line</span>
                    <LineDropdownSelector isDarkStyle={true} />
                  </div>
                  <div className="ir-toolbar__item ir-toolbar__status">
                    <span className="filterLabel">Status</span>
                    <Select
                      value={selectedStatus}
                      onChange={handleStatusChange}
                      options={statusFilterOptions}
                      placeholder="All"
                      isClearable
                      classNamePrefix="ir-status-filter"
                    />
                  </div>
                  <div className="ir-toolbar__item ir-toolbar__seq">
                    <span className="filterLabel">From Station</span>
                    <input
                      type="text"
                      className={`ir-seq-input${fromSeqError ? ' ir-seq-input--error' : ''}`}
                      value={selectedFromSequence}
                      onChange={handleFromSeqChange}
                      placeholder="0-99"
                      disabled={selectedStatus?.value === 'READY_FOR_DROP'}
                      data-testid="from-seq-input"
                    />
                    {fromSeqError && (
                      <span className="ir-seq-error-msg">{fromSeqError}</span>
                    )}
                  </div>
                  <div className="ir-toolbar__item ir-toolbar__seq">
                    <span className="filterLabel">To Station</span>
                    <input
                      type="text"
                      className={`ir-seq-input${toSeqError ? ' ir-seq-input--error' : ''}`}
                      value={selectedToSequence}
                      onChange={handleToSeqChange}
                      placeholder="0-99"
                      disabled={selectedStatus?.value === 'READY_FOR_DROP'}
                      data-testid="to-seq-input"
                    />
                    {toSeqError && (
                      <span className="ir-seq-error-msg">{toSeqError}</span>
                    )}
                  </div>
                  <div className="ir-toolbar__item ir-toolbar__buttons">
                    <div className="buttonsContainer">
                      <button
                        className="searchFilterBtn"
                        onClick={handleSearch}
                        title="Search"
                      >
                        Search
                      </button>
                      {isFilterActive && (
                        <button
                          className="clearFilterBtn"
                          onClick={handleClearFilters}
                          title="Clear all filters"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="ir-toolbar__item ir-toolbar__attributes">
                    <AttributesSelector
                      columns={optionalColumns}
                      selectedOptionalCols={selectedOptionalColNames}
                      applyColumnsSelection={(cols) => setSelectedOptionalColNames(cols)}
                    />
                  </div>
                  <div className="ir-toolbar__item ir-toolbar__logo">
                    <Link to="/">
                      <img
                        src={Ipms}
                        alt="IPMS"
                        width="100%"
                        className="logo_img"
                      />
                    </Link>
                  </div>
              </div>
              <table
                width="100%"
                border="0"
                align="center"
                cellPadding="0"
                cellSpacing="0"
                className="inner-table"
              >
                {appliedStatus !== 'DROPPED' && (
                  <>
                    <thead>
                      <tr>
                        <td
                          colSpan={6 + activeOptionalColumns.length}
                          align="left"
                          valign="middle"
                          className="droprow"
                        >
                          Ready For Drop
                        </td>
                      </tr>
                      <tr className="asnHeading">
                        <td width="7%" align="left" valign="middle">
                          &nbsp;
                        </td>
                        <td width="5%" align="left" valign="middle">
                          &nbsp;
                        </td>
                        <td width="8%" align="center" valign="middle">
                          Action
                        </td>
                        <td width="10%" height="30" align="center" valign="middle">
                          ASN
                        </td>
                        <td
                          width={activeOptionalColumns.length > 0 ? '30%' : '45%'}
                          align="left"
                          valign="middle"
                          className="innerTableDescriptionCell"
                        >
                          {parentName} Description
                        </td>
                        <td width={activeOptionalColumns.length > 0 ? '15%' : '25%'} align="left" valign="middle">
                          {parentName}
                        </td>
                        {activeOptionalColumns.map((col) => (
                          <td
                            key={col.name}
                            align="center"
                            valign="middle"
                            style={{ minWidth: '110px', padding: '0 16px', whiteSpace: 'nowrap' }}
                          >
                            {col.title}
                          </td>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredReadyForDropData.length > 0
                        ? [...Array(READY_FOR_DROP_ROWS_COUNT).keys()].map(index =>
                            renderReadyForDropRow(index),
                          )
                        : <tr><td colSpan={6 + activeOptionalColumns.length} align="center" className="noDataCell">No data</td></tr>
                      }
                    </tbody>
                  </>
                )}

                {appliedStatus !== 'READY_FOR_DROP' && (
                  <>
                    <thead>
                      <tr>
                        <td
                          colSpan={6 + activeOptionalColumns.length}
                          align="left"
                          valign="middle"
                          className="droprow droppedHeading"
                        >
                          DROPPED CHASSIS
                        </td>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDroppedData.length > 0
                        ? [...Array(FILTERED_DROPPED_ROWS_COUNT).keys()].map(renderRow)
                        : <tr><td colSpan={6 + activeOptionalColumns.length} align="center" className="noDataCell">No data</td></tr>
                      }
                    </tbody>
                  </>
                )}
              </table>
            </td>
          </tr>
          <tr align="center" valign="middle">
            <td
              height="60"
              className="footerCell"
            >
              Last Refreshed:{' '}
              {lastRefreshedDate
                ? lastRefreshedDate.format('hh:mmA [(IST)] DD MMMM YYYY')
                : '--:--'}
            </td>
          </tr>
        </table>
      </div>
    </div>
  );
};

export default SubAssemblyView;
